import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["use", "children"];
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { filterProps } from 'foundations-components/utils/filterProps';
import styled from 'foundations-theming/utils';
import { mobileScreen, breakpoint } from '../constants/Breakpoints';

/**
 * Props for {@link PageSection}.
 *
 * Note on margins: `PageSection` accepts `MarginStyleProps` (e.g.
 * `marginBlockEnd`, `marginBlock`, etc.) so that callers can occasionally
 * tighten or loosen vertical rhythm between sections, but the default
 * `marginBlockEnd` token is intentionally consistent across pages so all
 * sections align to the same spacing rhythm. Only override when there is
 * a strong design reason, and always pass Trellis space tokens (e.g.
 * `"300"`, `"600"`) — never raw px values via `style`.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const StyledSection = styled.section.withConfig({
  displayName: "PageSection__StyledSection"
})(({
  theme,
  $cssStyles
}) => Object.assign({
  inlineSize: '100%',
  position: 'relative',
  marginBlockEnd: theme.space[1200],
  paddingInline: theme.space[0],
  [breakpoint[mobileScreen]]: {
    paddingInline: theme.space[300]
  }
}, $cssStyles));

/**
 * A page section wrapper that owns the consistent vertical rhythm between
 * sections (default `marginBlockEnd: theme.space[1200]`). Prefer the
 * default spacing — only override `marginBlockEnd` (or other margin
 * tokens) when there is a strong design reason, and always use Trellis
 * space tokens rather than raw px values.
 */
export const PageSection = _ref => {
  let {
      use,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(Object.assign({}, tokenProps, {
    backgroundColor: use === 'shaded' ? 'fill-surface-recessed' : use === 'default' ? 'fill-surface-default' : undefined
  }));
  return /*#__PURE__*/_jsx(StyledSection, Object.assign({}, domProps, {
    $cssStyles: Object.assign({}, cssStyles),
    children: children
  }));
};
export const SectionHeader = styled.div.withConfig({
  displayName: "PageSection__SectionHeader"
})(({
  theme,
  maxInlineSize
}) => {
  return {
    textAlign: 'center',
    maxWidth: maxInlineSize,
    marginBlockEnd: theme.space[800],
    marginInline: 'auto'
  };
});
export const SectionTitle = props => {
  return /*#__PURE__*/_jsx(Text, Object.assign({}, props, {
    as: "h2",
    typography: "heading-400",
    paddingBlockEnd: "200",
    marginBlockStart: "0"
  }));
};
export const SectionSubTitle = props => {
  return /*#__PURE__*/_jsx(Text, Object.assign({}, props, {
    as: "p"
  }));
};
export const SectionContainer = props => {
  return /*#__PURE__*/_jsx(Flex, Object.assign({}, props, {
    maxInlineSize: "1200px",
    marginInline: "auto",
    inlineSize: "100%",
    flexDirection: "column"
  }));
};